import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Paper,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

interface Currency {
  country: string;
  currency: string;
  sell: number;
  buy: number;
  date: string;
}

const emptyForm = {
  country: "",
  currency: "",
  sell: "",
  buy: "",
  date: "",
};

const CurrencyAdminForm: React.FC = () => {
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [form, setForm] = useState<any>(emptyForm);
  const [editing, setEditing] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialog, setDeleteDialog] = useState<string | null>(null);

  const fetchCurrencies = async () => {
    const res = await fetch("/api/currencies/");
    setCurrencies(await res.json());
  };

  useEffect(() => {
    fetchCurrencies();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuccess(null);
    setError(null);
    try {
      const method = editing ? "PUT" : "POST";
      const url = editing
        ? `/api/currencies/${encodeURIComponent(editing)}`
        : "/api/currencies/";
      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          sell: parseFloat(form.sell),
          buy: parseFloat(form.buy),
          date: new Date(form.date).toISOString(),
        }),
      });
      if (!res.ok) throw new Error("Failed to save currency");
      setSuccess(editing ? "Currency updated!" : "Currency added!");
      setForm(emptyForm);
      setEditing(null);
      fetchCurrencies();
    } catch (err: any) {
      setError(err.message || "Error occurred");
    }
  };

  const handleEdit = (currency: Currency) => {
    setForm({
      country: currency.country,
      currency: currency.currency,
      sell: currency.sell,
      buy: currency.buy,
      date: currency.date.slice(0, 16), // for datetime-local input
    });
    setEditing(currency.country);
  };

  const handleDelete = async () => {
    if (!deleteDialog) return;
    try {
      const res = await fetch(
        `/api/currencies/${encodeURIComponent(deleteDialog)}`,
        {
          method: "DELETE",
        }
      );
      if (!res.ok) throw new Error("Failed to delete currency");
      setSuccess("Currency deleted!");
      setDeleteDialog(null);
      fetchCurrencies();
    } catch (err: any) {
      setError(err.message || "Error occurred");
    }
  };

  return (
    <Box sx={{ maxWidth: 800, mx: "auto", mt: 4 }}>
      <Typography variant="h5" gutterBottom>
        Manage Currencies
      </Typography>
      <form onSubmit={handleSubmit}>
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
          <TextField
            label="Country"
            name="country"
            value={form.country}
            onChange={handleChange}
            fullWidth
            required
            disabled={!!editing}
          />
          <TextField
            label="Currency"
            name="currency"
            value={form.currency}
            onChange={handleChange}
            fullWidth
            required
          />
          <TextField
            label="Sell"
            name="sell"
            type="number"
            value={form.sell}
            onChange={handleChange}
            fullWidth
            required
          />
          <TextField
            label="Buy"
            name="buy"
            type="number"
            value={form.buy}
            onChange={handleChange}
            fullWidth
            required
          />
          <TextField
            label="Date"
            name="date"
            type="datetime-local"
            value={form.date}
            onChange={handleChange}
            fullWidth
            required
            InputLabelProps={{ shrink: true }}
          />
          <Button
            type="submit"
            variant="contained"
            color="primary"
            sx={{ minWidth: 120 }}
          >
            {editing ? "Update" : "Add"}
          </Button>
          {editing && (
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => {
                setEditing(null);
                setForm(emptyForm);
              }}
            >
              Cancel
            </Button>
          )}
        </Box>
      </form>
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Country</TableCell>
              <TableCell>Currency</TableCell>
              <TableCell>Sell</TableCell>
              <TableCell>Buy</TableCell>
              <TableCell>Date</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {currencies.map((currency) => (
              <TableRow key={currency.country}>
                <TableCell>{currency.country}</TableCell>
                <TableCell>{currency.currency}</TableCell>
                <TableCell>{currency.sell}</TableCell>
                <TableCell>{currency.buy}</TableCell>
                <TableCell>
                  {new Date(currency.date).toLocaleString()}
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    onClick={() => handleEdit(currency)}
                    color="primary"
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    onClick={() => setDeleteDialog(currency.country)}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog open={!!deleteDialog} onClose={() => setDeleteDialog(null)}>
        <DialogTitle>Delete Currency</DialogTitle>
        <DialogContent>
          Are you sure you want to delete <b>{deleteDialog}</b>?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(null)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CurrencyAdminForm;
